<template>
  <!-- Carrito lateral. HTML + CSS plano (sin v-navigation-drawer / v-overlay) para que funcione igual en Windows 7
       (Chrome 109 / Firefox 115). El estado abierto/cerrado sigue siendo global.cartDrawer. -->
  <div class="ps-cart" :class="{ 'ps-cart--open': global.cartDrawer }" :inert="global.cartDrawer ? null : ''">
    <div class="ps-cart__backdrop" @click="close"></div>

    <aside class="ps-cart__panel" role="dialog" aria-modal="true" aria-label="Tu carrito">
      <!-- Header -->
      <header class="ps-cart__header">
        <div class="ps-cart__title">
          <i class="mdi mdi-cart-outline" aria-hidden="true"></i>
          <span>Tu carrito</span>
          <span v-if="cartItems.length" class="ps-cart__count">{{ itemCount }}</span>
        </div>

        <button type="button" class="ps-cart__close" aria-label="Cerrar el carrito" @click="close">
          <i class="mdi mdi-close" aria-hidden="true"></i>
        </button>
      </header>

      <!-- Loading -->
      <div v-if="global.cartLoading && !cartItems.length" class="ps-cart__empty">
        <span class="ps-spinner" role="status" aria-label="Cargando"></span>
      </div>

      <!-- Carrito vacío -->
      <div v-else-if="cartItems.length === 0" class="ps-cart__empty">
        <div class="ps-cart__empty-icon"><i class="mdi mdi-cart-outline" aria-hidden="true"></i></div>
        <div class="ps-cart__empty-title">Tu carrito está vacío</div>
        <p class="ps-cart__empty-text">Agregá productos para comenzar tu compra.</p>
        <button type="button" class="ps-btn ps-btn--ghost" @click="close">Seguir comprando</button>
      </div>

      <!-- Productos -->
      <ul v-else class="ps-cart__items">
        <li v-for="item in cartItems" :key="item.cartItemId" class="ps-cart__item">
          <div class="ps-cart__item-image">
            <img :src="item.image" :alt="item.name" />
          </div>

          <div class="ps-cart__item-info">
            <div class="ps-cart__item-name">{{ item.name }}</div>
            <div v-if="item.variant" class="ps-cart__item-variant">{{ item.variant }}</div>

            <div class="ps-cart__item-row">
              <!-- Stepper de cantidad -->
              <div class="ps-qty">
                <button
                  type="button"
                  class="ps-qty__btn"
                  aria-label="Una unidad menos"
                  :disabled="item.quantity <= 1 || updating.has(item.cartItemId)"
                  @click="changeQuantity(item, item.quantity - 1)"
                >
                  <i class="mdi mdi-minus" aria-hidden="true"></i>
                </button>
                <span class="ps-qty__value">{{ item.quantity }}</span>
                <button
                  type="button"
                  class="ps-qty__btn"
                  aria-label="Una unidad más"
                  :disabled="updating.has(item.cartItemId)"
                  @click="changeQuantity(item, item.quantity + 1)"
                >
                  <i class="mdi mdi-plus" aria-hidden="true"></i>
                </button>
              </div>

              <div class="ps-cart__item-price">
                <div class="ps-cart__item-total">{{ formatCurrency(item.lineTotal) }}</div>
                <div v-if="item.unitDiscountPrice < item.unitPrice" class="ps-cart__item-save">
                  {{ formatCurrency(item.unitDiscountPrice * item.quantity) }} con el medio de pago más conveniente
                </div>
              </div>
            </div>
          </div>

          <button
            type="button"
            class="ps-cart__item-remove"
            aria-label="Quitar del carrito"
            :disabled="removing.has(item.cartItemId)"
            @click="removeItem(item.cartItemId)"
          >
            <i class="mdi mdi-close" aria-hidden="true"></i>
          </button>
        </li>
      </ul>

      <!-- Footer -->
      <footer v-if="cartItems.length > 0" class="ps-cart__footer">
        <div class="ps-cart__subtotal">
          <span>Subtotal</span>
          <strong>{{ formatCurrency(total) }}</strong>
        </div>
        <p class="ps-cart__note">El envío se calcula en el checkout.</p>

        <button type="button" class="ps-btn ps-btn--primary ps-btn--block" @click="goCheckout">
          Ir al checkout
          <i class="mdi mdi-arrow-right" aria-hidden="true"></i>
        </button>
      </footer>
    </aside>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from "vue";

const global = window.useGlobalStore();

const cartItems = computed(() => global.cartItems || []);
const total = computed(() => global.cartTotal);
const itemCount = computed(() => global.cartCount);

const updating = ref(new Set());
const removing = ref(new Set());

function close() {
  global.cartDrawer = false;
}

// Esc cierra el panel; mientras está abierto no se desplaza la página de atrás.
function onKey(e) {
  if (e.key === "Escape" && global.cartDrawer) close();
}

watch(
  () => global.cartDrawer,
  (open) => {
    document.documentElement.classList.toggle("ps-no-scroll", !!open);
  },
);

onMounted(() => document.addEventListener("keydown", onKey));
onBeforeUnmount(() => {
  document.removeEventListener("keydown", onKey);
  document.documentElement.classList.remove("ps-no-scroll");
});

function formatCurrency(value) {
  return (value || 0).toLocaleString("es-AR", {
    style: "currency",
    currency: "ARS",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

async function changeQuantity(item, quantity) {
  if (quantity < 1) return;
  updating.value.add(item.cartItemId);
  try {
    await global.updateCartItemQuantity(item.cartItemId, quantity);
  } finally {
    updating.value.delete(item.cartItemId);
  }
}

async function removeItem(cartItemId) {
  removing.value.add(cartItemId);
  try {
    await global.removeItemFromCart(cartItemId);
  } finally {
    removing.value.delete(cartItemId);
  }
}

function goCheckout() {
  window.location.href = "/Home/Checkout";
}
</script>

<style scoped>
.ps-cart {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 3000;
  visibility: hidden;
  pointer-events: none;
  transition: visibility 0s linear 0.25s;
}

.ps-cart--open {
  visibility: visible;
  pointer-events: auto;
  transition-delay: 0s;
}

.ps-cart__backdrop {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: rgba(26, 26, 0, 0.5);
  opacity: 0;
  transition: opacity 0.25s ease;
}

.ps-cart--open .ps-cart__backdrop {
  opacity: 1;
}

.ps-cart__panel {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 420px;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  background: #f4efe6;
  color: #1a1a00;
  box-shadow: -8px 0 28px rgba(0, 0, 0, 0.25);
  transform: translateX(100%);
  transition: transform 0.25s ease;
}

.ps-cart--open .ps-cart__panel {
  transform: translateX(0);
}

.ps-cart__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  background: #124b4e;
  color: #f4efe6;
}

.ps-cart__title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: var(--ps-font-title, "Poppins", Arial, sans-serif);
  font-size: 17px;
  font-weight: 700;
}

.ps-cart__title .mdi {
  font-size: 22px;
  color: #a9d840;
}

.ps-cart__count {
  min-width: 22px;
  padding: 1px 7px;
  border-radius: 999px;
  background: #124b4e;
  color: #ffffff;
  font-size: 12px;
  font-weight: 700;
  text-align: center;
}

.ps-cart__close {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: inherit;
  font-size: 20px;
  cursor: pointer;
}

.ps-cart__close:hover {
  background: rgba(244, 239, 230, 0.15);
}

.ps-cart__empty {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px;
  text-align: center;
}

.ps-cart__empty-icon {
  width: 72px;
  height: 72px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(18, 75, 78, 0.14);
  color: #124b4e;
  font-size: 36px;
}

.ps-cart__empty-title {
  margin-top: 16px;
  font-family: var(--ps-font-title, "Poppins", Arial, sans-serif);
  font-size: 18px;
  font-weight: 700;
}

.ps-cart__empty-text {
  margin: 8px 0 24px;
  font-size: 14px;
  color: rgba(26, 26, 0, 0.65);
}

.ps-cart__items {
  flex: 1 1 auto;
  margin: 0;
  padding: 8px 12px;
  list-style: none;
  overflow-y: auto;
}

.ps-cart__item {
  position: relative;
  display: flex;
  gap: 12px;
  padding: 12px 8px;
  border-bottom: 1px solid rgba(26, 26, 0, 0.08);
}

.ps-cart__item-image {
  flex: 0 0 72px;
  width: 72px;
  height: 72px;
  overflow: hidden;
  border-radius: 10px;
  background: #fff;
}

.ps-cart__item-image img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.ps-cart__item-info {
  flex: 1 1 auto;
  min-width: 0;
  padding-right: 22px;
}

.ps-cart__item-name {
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  font-family: var(--ps-font-title, "Poppins", Arial, sans-serif);
  font-size: 14px;
  font-weight: 600;
  line-height: 1.3;
}

.ps-cart__item-variant {
  margin-top: 2px;
  font-size: 12px;
  color: rgba(26, 26, 0, 0.6);
}

.ps-cart__item-row {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 8px;
  margin-top: 8px;
}

.ps-cart__item-price {
  text-align: right;
}

.ps-cart__item-total {
  font-family: var(--ps-font-title, "Poppins", Arial, sans-serif);
  font-size: 14px;
  font-weight: 700;
}

.ps-cart__item-save {
  font-size: 11.5px;
  color: #2f8a1c;
}

.ps-cart__item-remove {
  position: absolute;
  top: 8px;
  right: 2px;
  width: 26px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: rgba(26, 26, 0, 0.55);
  font-size: 16px;
  cursor: pointer;
}

.ps-cart__item-remove:hover {
  background: rgba(26, 26, 0, 0.08);
  color: #1a1a00;
}

.ps-cart__item-remove:disabled {
  opacity: 0.4;
}

.ps-cart__footer {
  padding: 16px;
  border-top: 1px solid rgba(26, 26, 0, 0.1);
  background: #fff;
}

.ps-cart__subtotal {
  display: flex;
  justify-content: space-between;
  font-size: 15px;
}

.ps-cart__subtotal strong {
  font-family: var(--ps-font-title, "Poppins", Arial, sans-serif);
}

.ps-cart__note {
  margin: 4px 0 14px;
  font-size: 12.5px;
  color: rgba(26, 26, 0, 0.6);
}
</style>
